import React from "react";
import PrimaryButton from "web/react/components/buttons/primary-button/primary-button";
import ContinueToOption from "web/react/components/continue-to-option/continue-to-option";
import SendToAppModal from "web/react/components/send-to-app-modal/send-to-app-modal";
import { useOverlayWatcher } from "web/react/hooks/use-overlay-watcher/use-overlay-watcher";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import environment from "web/script/modules/environment";
import globals from "web/script/modules/globals";
import userProfiler from "web/script/modules/userprofiler";
import { getActiveBlock } from "web/script/utils/active-campaigns";
import { RawBranchLink } from "web/script/utils/branch-io";
import browser from "web/script/utils/browser";
import history from "web/script/utils/history";
import storage from "web/script/utils/storage";
import styles from "./related-products-show-more.css";

const HARD_BLOCK = "hard_block";
const NO_BLOCK = "no_block";
const SOFT_BLOCK = "soft_block";

interface RelatedProductsSectionProp {
    view_more_url: string;
    product_is_instock?: boolean;
    id: string;
    isAffiliate: boolean;
}

export function RelatedProductsShowMore(section: RelatedProductsSectionProp): JSX.Element {
    const productIsInstock = section.product_is_instock != null && section.product_is_instock;

    const appModalName = `send-to-app-related-show-more-modal-${section.id}`;

    const branchLink = productIsInstock
        ? RawBranchLink.InStockShowMore
        : RawBranchLink.OutOfStockShowMore;

    const blockingTitle = productIsInstock
        ? // "Shop the world’s biggest and best selection of fashion"
          gettext("in_stock_product.related_show_more.app_overlay_block.title_1")
        : // "The hottest and newest products from over 12,000 brands"
          gettext("oos_product.related_show_more.app_overlay_block.title_2");

    function onBlockModalOpen(): void {
        // I check the current overlay state instead of modalIsOpen
        // to get around the delay introduced by hooks
        if (history.getCurrentState().overlay !== appModalName) {
            history.pushOverlay(appModalName);
        }
    }

    function getBlockVariant(): string {
        const variant = getActiveBlock("mg_pdp_show_more_block");

        if (
            !analytics.isPaidChannel() &&
            variant &&
            section.isAffiliate &&
            browser.isSupportedMobileDevice &&
            (!userProfiler.isLoggedIn() || environment.getFeature("mg_block_logged_in_users")) &&
            environment.get("domainDefaultLanguageIsEnglish")
        ) {
            return variant;
        } else {
            return NO_BLOCK;
        }
    }

    const blockVariant = getBlockVariant();

    function onButtonClick(e: React.MouseEvent): void {
        analytics.event("navigation", "show_more_related", "show_feed");
        const isPlaUtmCampaign = environment.get("isPlaUtmCampaign") && productIsInstock;

        if ([HARD_BLOCK, SOFT_BLOCK].includes(blockVariant) && !isPlaUtmCampaign) {
            e.preventDefault();
            onBlockModalOpen();
            return;
        }
    }

    function onBlockModalClose(): void {
        history.popOverlay(appModalName);
    }

    const isBlockModalOpen = !!useOverlayWatcher(appModalName);

    return (
        <div>
            <PrimaryButton
                href={section.view_more_url}
                className={styles.showMoreButton}
                onClick={onButtonClick}
            >
                {gettext("product.related_products.show_me_more")}
            </PrimaryButton>
            <SendToAppModal
                title={blockingTitle}
                url={branchLink}
                isOpen={isBlockModalOpen}
                onClose={onBlockModalClose}
                analyticsEventLabel={"oos_related_show_more"}
                extraContent={() =>
                    blockVariant === SOFT_BLOCK ? (
                        <ContinueToOption
                            continueMessage={gettext("general.continue_shopping_btn_text")}
                            analyticsEventLabel={"feeds_show_more"}
                            continueClick={async () => {
                                onBlockModalClose();
                                storage.set("continueClicked", true, true);
                                globals.window.location.href = section.view_more_url;
                            }}
                        />
                    ) : null
                }
            />
        </div>
    );
}

export default RelatedProductsShowMore;
